import { ValidationResult, FrontmatterMetadata } from '../types.js';

/**
 * Validates the format and structure of metadata, not business rules.
 * Only checks that required fields exist and are of the correct type.
 */
export class MetadataFormatValidator {
  /**
   * Validates that required fields exist and are of the correct type
   */
  validateFormat(metadata: any): ValidationResult {
    const errors: string[] = [];

    // Check if metadata is an object
    if (typeof metadata !== 'object' || metadata === null) {
      errors.push('Metadata must be an object');
      return {
        valid: false,
        errors,
        warnings: []
      };
    }

    // Validate required fields
    const requiredFields = ['id', 'type', 'active'];
    for (const field of requiredFields) {
      if (!(field in metadata)) {
        errors.push(`Missing required field: ${field}`);
      }
    }

    // Validate field types and formats
    if ('id' in metadata) {
      if (typeof metadata.id !== 'string') {
        errors.push('Field id must be a string');
      } else if (!metadata.id.match(/^[a-zA-Z0-9-]+$/)) {
        errors.push('Field id must contain only alphanumeric characters and hyphens');
      }
    }

    if ('type' in metadata) {
      if (typeof metadata.type !== 'string') {
        errors.push('Field type must be a string');
      } else if (!metadata.type.match(/^[a-zA-Z0-9-]+$/)) {
        errors.push('Field type must contain only alphanumeric characters and hyphens');
      }
    }

    if ('active' in metadata && typeof metadata.active !== 'boolean') {
      errors.push('Field active must be a boolean');
    }

    // Validate optional state field when present and not undefined
    if ('state' in metadata && metadata.state !== undefined) {
      if (typeof metadata.state !== 'string') {
        errors.push('Field state must be a string');
      } else if (!metadata.state.match(/^[a-zA-Z0-9-]+$/)) {
        errors.push('Field state must contain only alphanumeric characters and hyphens');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings: []
    };
  }

  /**
   * Clean metadata by removing invalid fields and normalizing values
   */
  cleanMetadata(meta: Partial<FrontmatterMetadata>): FrontmatterMetadata {
    // Ensure required fields are present
    if (meta.id === undefined || meta.type === undefined || typeof meta.active !== 'boolean') {
      throw new Error('Required fields missing: id, type, and active must be present');
    }

    // Start with required fields
    const cleaned: FrontmatterMetadata = {
      id: meta.id,  // Preserve ID case
      type: meta.type.toLowerCase(),
      active: meta.active
    };

    // Add optional fields if present and not undefined
    if (meta.state !== undefined) {
      cleaned.state = meta.state.toLowerCase();
    }

    // Copy any other fields
    for (const [key, value] of Object.entries(meta)) {
      if (!['id', 'type', 'active', 'state'].includes(key) && value !== undefined) {
        cleaned[key] = value;
      }
    }

    return cleaned;
  }
} 